import type { ReactNode } from "react";
import { px } from "@/components/tool-kit/charts/marks";
import { estimateTextWidth } from "@/lib/tool-kit/charts/text";
import type { PedigreePerson } from "@/lib/tool-kit/ecology/pedigree";
import type { UsedFeatures } from "@/lib/tool-kit/ecology/pedigree-layout";
import { CHART_INK } from "@/lib/tool-kit/palette";
import { PersonGlyph, type SymbolPaint } from "./pedigree-symbols";

/*
 * The key (legend) under a pedigree. Bennett et al. (2008) ask for a key that defines
 * every fill and symbol the pedigree uses, so it lists exactly the ones that appear.
 */

export interface KeyLabels {
  heading: string;
  male: string;
  female: string;
  unknown: string;
  affected: string;
  carrier: string;
  presymptomatic: string;
  deceased: string;
  proband: string;
  consultand: string;
  consanguineous: string;
  separated: string;
  adopted: string;
  mz: string;
  dz: string;
  pregnancy: string;
  miscarriage: string;
  termination: string;
  stillbirth: string;
  /** The letter beside the proband arrow and inside a pregnancy symbol. */
  probandLetter: string;
  pregnancyLetter: string;
}

interface Item {
  id: string;
  text: string;
  icon: ((x: number, y: number) => ReactNode) | null;
}

const ICON_WIDTH = 50;
const ITEM_GAP = 20;
const ROW = 30;
const MARGIN = 20;
const FONT = 12;
const SCALE = 0.55;

function sample(overrides: Partial<PedigreePerson>): PedigreePerson {
  return {
    name: "",
    sex: "f",
    status: "unaffected",
    kind: "person",
    deceased: false,
    proband: false,
    consultand: false,
    adopted: false,
    twin: null,
    born: null,
    died: null,
    age: null,
    notes: [],
    ...overrides,
  };
}

function glyph(
  person: PedigreePerson,
  paint: SymbolPaint,
  labels: KeyLabels
): (x: number, y: number) => ReactNode {
  return (x, y) => (
    <g transform={`translate(${px(x)} ${px(y)}) scale(${SCALE})`}>
      <PersonGlyph
        labels={{
          pregnancy: labels.pregnancyLetter,
          proband: labels.probandLetter,
        }}
        paint={paint}
        person={person}
        x={0}
        y={0}
      />
    </g>
  );
}

function lines(...paths: string[]): (x: number, y: number) => ReactNode {
  return (x, y) => (
    <g
      fill="none"
      stroke={CHART_INK.text}
      strokeWidth={1.6}
      transform={`translate(${px(x)} ${px(y)})`}
    >
      {paths.map((path) => (
        <path d={path} key={path} />
      ))}
    </g>
  );
}

const DOUBLE = lines("M-14 -3H14", "M-14 3H14");
const SEPARATED = lines("M-14 0H14", "M-6 6L-2 -6", "M2 6L6 -6");
const DZ = lines("M0 -9L-10 9", "M0 -9L10 9");
const MZ = lines("M0 -9L-10 9", "M0 -9L10 9", "M-5 0H5");

export function keyItems(
  used: UsedFeatures,
  labels: KeyLabels,
  paint: SymbolPaint
): Item[] {
  const g = (overrides: Partial<PedigreePerson>) =>
    glyph(sample(overrides), paint, labels);
  const items: Item[] = [];
  if (used.sexes.has("m")) {
    items.push({ id: "m", text: labels.male, icon: g({ sex: "m" }) });
  }
  if (used.sexes.has("f")) {
    items.push({ id: "f", text: labels.female, icon: g({ sex: "f" }) });
  }
  if (used.sexes.has("u")) {
    items.push({ id: "u", text: labels.unknown, icon: g({ sex: "u" }) });
  }
  if (used.statuses.has("affected")) {
    items.push({
      id: "aff",
      text: labels.affected,
      icon: g({ status: "affected" }),
    });
  }
  if (used.statuses.has("carrier")) {
    items.push({
      id: "car",
      text: labels.carrier,
      icon: g({ status: "carrier" }),
    });
  }
  if (used.statuses.has("presymptomatic")) {
    items.push({
      id: "pre",
      text: labels.presymptomatic,
      icon: g({ status: "presymptomatic" }),
    });
  }
  if (used.deceased) {
    items.push({
      id: "dec",
      text: labels.deceased,
      icon: g({ deceased: true }),
    });
  }
  if (used.proband) {
    items.push({ id: "pro", text: labels.proband, icon: g({ proband: true }) });
  }
  if (used.consultand) {
    items.push({
      id: "con",
      text: labels.consultand,
      icon: g({ consultand: true }),
    });
  }
  if (used.adopted) {
    items.push({
      id: "ado",
      text: labels.adopted,
      icon: g({ adopted: true, sex: "m" }),
    });
  }
  if (used.relations.has("consanguineous")) {
    items.push({ id: "cons", text: labels.consanguineous, icon: DOUBLE });
  }
  if (used.relations.has("separated")) {
    items.push({ id: "sep", text: labels.separated, icon: SEPARATED });
  }
  if (used.mz) {
    items.push({ id: "mz", text: labels.mz, icon: MZ });
  }
  if (used.dz) {
    items.push({ id: "dz", text: labels.dz, icon: DZ });
  }
  const kinds: [string, string, PedigreePerson["kind"]][] = [
    ["pregnancy", labels.pregnancy, "pregnancy"],
    ["miscarriage", labels.miscarriage, "miscarriage"],
    ["termination", labels.termination, "termination"],
  ];
  for (const [id, text, kind] of kinds) {
    if (used.kinds.has(kind)) {
      items.push({ id, text, icon: g({ kind }) });
    }
  }
  if (used.kinds.has("stillbirth")) {
    items.push({ id: "sb", text: labels.stillbirth, icon: null });
  }
  return items;
}

interface Placed {
  item: Item;
  x: number;
  row: number;
}

function arrange(items: readonly Item[], width: number): Placed[] {
  const placed: Placed[] = [];
  let x = MARGIN;
  let row = 0;
  for (const item of items) {
    const size =
      (item.icon ? ICON_WIDTH : 0) +
      estimateTextWidth(item.text, { fontSize: FONT }) +
      ITEM_GAP;
    if (x > MARGIN && x + size > width - MARGIN) {
      row += 1;
      x = MARGIN;
    }
    placed.push({ item, x, row });
    x += size;
  }
  return placed;
}

export function keyHeight(
  used: UsedFeatures,
  width: number,
  labels: KeyLabels
): number {
  const items = keyItems(used, labels, {
    fill: CHART_INK.text,
    carrier: "dot",
  });
  if (items.length === 0) {
    return 0;
  }
  const rows = (arrange(items, width).at(-1)?.row ?? 0) + 1;
  return 30 + rows * ROW + 8;
}

export function PedigreeKey({
  used,
  labels,
  paint,
  width,
}: {
  used: UsedFeatures;
  labels: KeyLabels;
  paint: SymbolPaint;
  width: number;
}) {
  const items = keyItems(used, labels, paint);
  if (items.length === 0) {
    return null;
  }
  const placed = arrange(items, width);
  return (
    <g>
      <line
        stroke={CHART_INK.grid}
        strokeWidth={1}
        x1={MARGIN}
        x2={px(width - MARGIN)}
        y1={4}
        y2={4}
      />
      <text
        fill={CHART_INK.text}
        fontSize={FONT}
        fontWeight="bold"
        x={MARGIN}
        y={22}
      >
        {labels.heading}
      </text>
      {placed.map(({ item, x, row }) => {
        const cy = 30 + row * ROW + ROW / 2;
        return (
          <g key={item.id}>
            {item.icon ? item.icon(x + ICON_WIDTH / 2, cy) : null}
            <text
              fill={CHART_INK.text}
              fontSize={FONT}
              x={px(x + (item.icon ? ICON_WIDTH : 0))}
              y={px(cy + FONT * 0.35)}
            >
              {item.text}
            </text>
          </g>
        );
      })}
    </g>
  );
}
